import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchMembersByMemberCategory } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

const memberClassLabel = (cls, t) => {
  switch (cls) {
    case "Collaborator": return t("member.class.Collaborator");
    case "Specialist": return t("member.class.Specialist");
    case "Investor": return t("member.class.Investor");
    case "ProductUser": return t("member.class.ProductUser");
    case "CompanyMember": return t("member.class.CompanyMember");
    default: return cls || "";
  }
};

const memberStateLabel = (state, t) => {
  switch (state) {
    case "Registered": return t("member.state.Registered");
    case "Assigned": return t("member.state.Assigned");
    case "Released": return t("member.state.Released");
    default: return state || "";
  }
};

export default function MemberCategoryMemberListPage() {
  const { businessDomainId, categoryId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const basePath = `/strategy/${businessDomainId}/member-categories/${categoryId}`;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMembersByMemberCategory(categoryId);
      setRows(
        (Array.isArray(data) ? data : []).map((m) => ({
          ...m,
          fullName: `${m.lastName || ""} ${m.firstName || ""}`.trim(),
          memberClassLabel: memberClassLabel(m.memberClass, t),
          memberStateLabel: memberStateLabel(m.memberState, t),
        }))
      );
    } catch (e) { notify.apiError(e); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  useEffect(() => { loadData(); }, [loadData]);

  const columns = [
    { field: "fullName", headerName: t("member.name"), flex: 1, minWidth: 160 },
    { field: "memberClassLabel", headerName: t("member.class"), flex: 1, minWidth: 130 },
    { field: "memberStateLabel", headerName: t("member.state"), flex: 1, minWidth: 120 },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("member.list.title")}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(`${basePath}/members/create`)}
        >
          {t("common.add")}
        </Button>
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(r) => r.memberId}
        loading={loading}
        autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        disableRowSelectionOnClick
        onRowClick={(params) => navigate(`${basePath}/members/${params.row.memberId}`)}
        sx={{
          "& .MuiDataGrid-row": { cursor: "pointer" },
          "& .MuiDataGrid-row:hover": { backgroundColor: "action.hover" },
        }}
      />
      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/member-categories/${categoryId}`)}>{t("common.back")}</Button>
      </Box>
    </Box>
  );
}
